import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  X,
  CalendarCog,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  RotateCcw,
  UserMinus,
  Utensils,
} from 'lucide-react';
import {
  useFetchBoardSchedule,
  useSaveScheduleDay,
  useSaveWorkCalendar,
  useSaveDayOff,
  useSetUserDefaultLunch,
  useSetUserDefaultHours,
  useResetScheduleDay,
} from '../../../services/onboardingBoard';
import PickerMenu from './PickerMenu';
import TimeField, { formatTime } from './TimeField';

// The manager's schedule: a full-screen MONTH CALENDAR overlay (Nic
// 2026-08-08: the slideout's day-card stack was clunky — humans plan
// staffing on a month grid). The grid gives the bird's-eye view (who's on,
// default vs planned days); clicking a day opens its editor in
// the right rail, calendar still in view. Every edit saves instantly.

const CARD =
  'rounded-xl bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_rgba(15,23,42,0.05)] dark:!bg-surface-card dark:ring-1 dark:ring-white/10';

const DEFAULT_START = '08:00';
const DEFAULT_END = '17:00';

// The rail's collapsible card (Nic 2026-09-01, premium pass): every card is
// COLLAPSED by default and its header carries a one-line SUMMARY, so the
// rail reads as a status panel when closed and an editor when open. Open
// state is remembered per browser (RAIL_OPEN_KEY). `alwaysOpen` = a plain
// titled card (the Day off action stays open on top).
const RAIL_OPEN_KEY = 'onboarding-schedule-rail';
const readRailOpen = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(RAIL_OPEN_KEY) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};
const writeRailOpen = (state) => {
  try {
    localStorage.setItem(RAIL_OPEN_KEY, JSON.stringify(state));
  } catch {
    // Storage blocked (private mode): the session still works, just
    // without memory.
  }
};

const RailCard = ({
  title,
  titleClassName = 'text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500',
  summary = null,
  open = false,
  onToggle = null,
  alwaysOpen = false,
  // The day row's date must never truncate (Nic 2026-09-01); the summary
  // gives way instead.
  keepTitle = false,
  children,
}) => (
  // Collapsed: the WHOLE card is the hit target (Nic 2026-09-01 - the
  // header row alone left the padding dead). Open: only the header
  // collapses it, so clicks inside the body never fold the card.
  <div
    className={`${CARD} p-3 ${!alwaysOpen && !open ? 'cursor-pointer' : ''}`}
    onClick={!alwaysOpen && !open ? onToggle : undefined}
  >
    {alwaysOpen ? (
      <div className={`mb-2 ${titleClassName}`}>{title}</div>
    ) : (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        className="flex w-full items-center gap-2 text-left"
        title={open ? 'Collapse' : 'Expand'}
      >
        <span
          className={`${keepTitle ? 'shrink-0 whitespace-nowrap' : 'min-w-0 truncate'} ${titleClassName}`}
        >
          {title}
        </span>
        {summary ? (
          <span className="ml-auto flex min-w-0 shrink items-center gap-1.5 truncate text-[10px] font-medium text-slate-400 dark:text-slate-500">
            {summary}
          </span>
        ) : null}
        <ChevronDown
          size={12}
          className={`shrink-0 text-slate-300 transition-transform dark:text-slate-600 ${
            open ? 'rotate-180' : ''
          } ${summary ? '' : 'ml-auto'}`}
        />
      </button>
    )}
    {alwaysOpen || open ? (
      <div className={alwaysOpen ? '' : 'mt-2'}>{children}</div>
    ) : null}
  </div>
);

// Header summaries for the collapsed cards.
const DAY_NAMES = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun' };
const daysSummary = (days) => {
  const set = [...new Set(days || [])].sort((a, b) => a - b);
  if (!set.length) return 'No days';
  const ranges = [];
  let [start, prev] = [set[0], set[0]];
  for (const d of set.slice(1)) {
    if (d === prev + 1) {
      prev = d;
    } else {
      ranges.push([start, prev]);
      start = d;
      prev = d;
    }
  }
  ranges.push([start, prev]);
  return ranges
    .map(([a, b]) =>
      a === b
        ? DAY_NAMES[a]
        : b === a + 1
          ? `${DAY_NAMES[a]}, ${DAY_NAMES[b]}`
          : `${DAY_NAMES[a]}-${DAY_NAMES[b]}`,
    )
    .join(', ');
};

// PG time columns come back as "08:00:00"; the API wants "HH:MM".
const hhmm = (t) => (t ? String(t).slice(0, 5) : '');

// 'HH:MM' -> '1:00 PM' for lunch labels.
const fmt12 = (t) => {
  if (!t) return '';
  const [h, m] = String(t).split(':').map(Number);
  const d = new Date();
  d.setHours(h || 0, m || 0, 0, 0);
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const p2 = (n) => String(n).padStart(2, '0');
const toIso = (d) =>
  `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
const localToday = () => toIso(new Date());

// Never block a time pick (Nic 2026-08-11). Two behaviors, one helper:
//   follow: true  (lunch pairs) — EVERY start change drags the end along,
//     keeping the pair's previous length (a 1-2 lunch restarted at 11
//     becomes 11-12; then picking :30 minutes makes it 11:30-12:30). The
//     length isn't clamped to an hour — it's whatever Jorge set; following
//     just makes the common one-hour case effortless.
//   follow: false (working hours / shifts) — the end stays put unless the
//     pick would invert the pair, in which case it slides to keep the
//     previous length (changing a shift's opening hour must not move its
//     close for no reason).
// End edits NEVER move the start; an inverted end pick snaps to the
// nearest valid end (start + 5m) instead of blocking the save.
const minutesOf = (t) => {
  const [h, m] = String(t || '0:0')
    .split(':')
    .map(Number);
  return (h || 0) * 60 + (m || 0);
};
const clockOf = (mins) => {
  const c = Math.max(0, Math.min(23 * 60 + 55, mins));
  return `${p2(Math.floor(c / 60))}:${p2(c % 60)}`;
};
const pairOnChange = ({
  start,
  end,
  changed,
  prevStart,
  prevEnd,
  follow = false,
}) => {
  const prevDur = minutesOf(prevEnd) - minutesOf(prevStart);
  const dur = prevDur > 0 ? prevDur : 60;
  if (changed === 'start') {
    if (follow || minutesOf(start) >= minutesOf(end)) {
      return { start, end: clockOf(minutesOf(start) + dur) };
    }
    return { start, end };
  }
  return minutesOf(end) > minutesOf(start)
    ? { start, end }
    : { start, end: clockOf(minutesOf(start) + 5) };
};

const railDayLabel = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString([], {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ISO weekday (1 = Monday .. 7 = Sunday) for a YYYY-MM-DD.
const isoWeekdayOf = (iso) => {
  const d = new Date(`${iso}T12:00:00`).getDay();
  return d === 0 ? 7 : d;
};

// Chip order for the working-days control (Mon-first, like the org week).
const WORKING_DAY_CHIPS = [
  { value: 1, label: 'M' },
  { value: 2, label: 'T' },
  { value: 3, label: 'W' },
  { value: 4, label: 'T' },
  { value: 5, label: 'F' },
  { value: 6, label: 'S' },
  { value: 7, label: 'S' },
];

// The org work calendar card: the BOARD DAY (hours + which weekdays). The
// whole cadence engine follows this (arrival clock start, step close, the
// "Closed today" banner, clocks pausing on non-working days), and every
// person's own Hours can only NARROW inside it - so widening the day here
// (say, to 10 PM) is what lets a personal shift reach 8 PM (Nic
// 2026-09-01). The org lunch editor is gone (2026-08-13): everyone starts at
// 12-1 and Jorge sets real times per person on the Lunches card. Renders
// headless inside a RailCard.
const WorkingTimeCard = ({ workCalendar, busy, onSave }) => {
  const days = workCalendar.workingDays || [1, 2, 3, 4, 5, 6, 7];
  const toggleDay = (value) => {
    const next = days.includes(value)
      ? days.filter((d) => d !== value)
      : [...days, value].sort((a, b) => a - b);
    if (next.length === 0) return; // at least one working day
    onSave({ workingDays: next });
  };
  const conflicts = workCalendar.cadenceConflicts || [];
  const saveHours = (changed, v) => {
    const fixed = pairOnChange({
      start: changed === 'start' ? v : workCalendar.workingHoursStartLocal,
      end: changed === 'end' ? v : workCalendar.workingHoursEndLocal,
      changed,
      prevStart: workCalendar.workingHoursStartLocal,
      prevEnd: workCalendar.workingHoursEndLocal,
    });
    onSave({
      workingHoursStartLocal: fixed.start,
      workingHoursEndLocal: fixed.end,
    });
  };
  return (
    <>
      <div className="mb-2 text-[10px] text-slate-400 dark:text-slate-500">
        The board day, every week: lead clocks run inside these hours and
        days, and each person's own shift (under Hours) fits inside them.
        Widen the day here first if someone needs later hours.
      </div>
      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-medium text-slate-500 dark:text-slate-400">
        Hours
        <TimeField
          value={workCalendar.workingHoursStartLocal}
          disabled={busy}
          onChange={(v) => saveHours('start', v)}
          title="Opening hour"
        />
        <span>to</span>
        <TimeField
          value={workCalendar.workingHoursEndLocal}
          disabled={busy}
          onChange={(v) => saveHours('end', v)}
          title="Closing hour"
        />
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
          Days
        </span>
        {WORKING_DAY_CHIPS.map((chip) => {
          const on = days.includes(chip.value);
          return (
            <button
              key={chip.value}
              type="button"
              disabled={busy}
              onClick={() => toggleDay(chip.value)}
              title={
                on
                  ? 'Working day: click to close it every week'
                  : 'Closed every week: click to make it a working day'
              }
              className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold transition-colors disabled:opacity-50 ${
                on
                  ? 'bg-slate-800 text-white dark:bg-slate-200 dark:!text-slate-900'
                  : 'bg-slate-100 text-slate-400 hover:bg-slate-200 dark:bg-white/10 dark:text-slate-500 dark:hover:bg-white/15'
              }`}
            >
              {chip.label}
            </button>
          );
        })}
      </div>
      {conflicts.length > 0 ? (
        <div className="mt-2 rounded-lg bg-amber-50 px-2.5 py-2 dark:bg-amber-500/10">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-amber-700 dark:text-amber-400">
            Cadence conflict
          </div>
          <ul className="space-y-0.5 pl-3.5 text-[10px] leading-relaxed text-amber-700 dark:text-amber-400">
            {conflicts.map((msg) => (
              <li key={msg} className="list-disc">
                {msg}
              </li>
            ))}
          </ul>
          <div className="mt-1 text-[10px] text-amber-600/80 dark:text-amber-400/70">
            These steps keep their scheduled times and will miss if unworked.
            Adjust the cadence or the hours.
          </div>
        </div>
      ) : null}
    </>
  );
};

// Assignment (how arriving leads pick a Sales Rep) moved to the board's
// Sales settings menu (Nic 2026-10-07).

// Standing lunches (Nic 2026-08-10): each specialist's OWN default lunch,
// Jorge-set. A specific day's roster row can still override it ("2-3 for
// today"); blank here = the org default from Working time. Lives in the
// EVERY DAY zone because it applies to all days. Edits STAGE locally and
// commit on the check (Nic 2026-08-11) — the specialist's board updates
// only when Jorge locks the pair in.
// Per-person STANDING hours (Nic 2026-09-01), the Lunches card's twin: an
// unset person simply READS as the org working day; any edit saves a real
// pair for that person (clamped inside the org day server-side). A
// specific day's roster row can still override it for that day only.
const HoursCard = ({ specialists, workCalendar, busy, onSave }) => {
  const orgStart = workCalendar.workingHoursStartLocal || DEFAULT_START;
  const orgEnd = workCalendar.workingHoursEndLocal || DEFAULT_END;
  const [drafts, setDrafts] = useState({}); // userId -> {start, end}
  const editDraft = (sp, changed, value) => {
    const base = drafts[sp.userId] || {
      start: sp.workStartLocal || orgStart,
      end: sp.workEndLocal || orgEnd,
    };
    setDrafts((prev) => ({
      ...prev,
      [sp.userId]: pairOnChange({
        start: changed === 'start' ? value : base.start,
        end: changed === 'end' ? value : base.end,
        changed,
        prevStart: base.start,
        prevEnd: base.end,
        follow: true,
      }),
    }));
  };
  const clearDraft = (userId) =>
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  return (
    <>
      <div className="mb-2 text-[10px] text-slate-400 dark:text-slate-500">
        Each specialist's usual shift, every day, inside the board day (
        {formatTime(orgStart)} - {formatTime(orgEnd)}). Leads rotate to them
        only during these hours. A specific day's roster row can still
        override it for that day only. Times are board time (Central).
      </div>
      {specialists.length === 0 ? (
        <div className="text-[10px] text-slate-300 dark:text-slate-600">
          No specialists yet.
        </div>
      ) : (
        <div className="space-y-1.5">
          {specialists.map((sp) => {
            const draft = drafts[sp.userId] || null;
            const saved =
              sp.workStartLocal && sp.workEndLocal
                ? { start: sp.workStartLocal, end: sp.workEndLocal }
                : null;
            const shown = draft || saved || { start: orgStart, end: orgEnd };
            const dirty =
              !!draft &&
              (draft.start !== (saved?.start || orgStart) ||
                draft.end !== (saved?.end || orgEnd));
            return (
              <div
                key={sp.userId}
                className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500 dark:text-slate-400"
              >
                <span className="min-w-0 flex-1 truncate">{sp.name}</span>
                <TimeField
                  value={shown.start}
                  disabled={busy}
                  onChange={(v) => editDraft(sp, 'start', v)}
                  minTime={orgStart}
                  maxTime={orgEnd}
                  title="Usual shift start (saves when you confirm with the check)"
                />
                <span className="text-[10px]">to</span>
                <TimeField
                  value={shown.end}
                  disabled={busy}
                  onChange={(v) => editDraft(sp, 'end', v)}
                  minTime={orgStart}
                  maxTime={orgEnd}
                  title="Usual shift end (saves when you confirm with the check)"
                />
                {dirty ? (
                  <>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        onSave(sp.userId, draft.start, draft.end);
                        clearDraft(sp.userId);
                      }}
                      className="flex items-center gap-1 rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-emerald-600 dark:!text-white"
                      title="Save these hours. Rotation and their board update immediately"
                    >
                      <Check size={10} /> Set
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => clearDraft(sp.userId)}
                      className="rounded-md p-0.5 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                      title="Discard the unsaved change"
                    >
                      <X size={11} />
                    </button>
                  </>
                ) : saved ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onSave(sp.userId, null, null)}
                    className="rounded-md p-0.5 text-[10px] font-medium text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                    title="Clear their usual hours and follow the working day"
                  >
                    Full day
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

const LunchesCard = ({ specialists, busy, onSave }) => {
  const [drafts, setDrafts] = useState({}); // userId -> {start, end}
  const editDraft = (sp, changed, value) => {
    const base = drafts[sp.userId] || {
      start: sp.lunchStartLocal || '12:00',
      end: sp.lunchEndLocal || '13:00',
    };
    setDrafts((prev) => ({
      ...prev,
      [sp.userId]: pairOnChange({
        start: changed === 'start' ? value : base.start,
        end: changed === 'end' ? value : base.end,
        changed,
        prevStart: base.start,
        prevEnd: base.end,
        follow: true,
      }),
    }));
  };
  const clearDraft = (userId) =>
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  return (
    <>
      <div className="mb-2 text-[10px] text-slate-400 dark:text-slate-500">
        Each specialist's usual lunch, every day. New specialists start at 12 to
        1. A specific day's roster row can still override it for that day only.
        Times here are board time (Central); each person's board shows them in
        their own timezone.
      </div>
      {specialists.length === 0 ? (
        <div className="text-[10px] text-slate-300 dark:text-slate-600">
          No specialists yet.
        </div>
      ) : (
        <div className="space-y-1.5">
          {specialists.map((sp) => {
            const draft = drafts[sp.userId] || null;
            const saved =
              sp.lunchStartLocal && sp.lunchEndLocal
                ? { start: sp.lunchStartLocal, end: sp.lunchEndLocal }
                : null;
            // No "default" indirection (Nic 2026-08-13): an unset person
            // just READS as 12-1 (the resolver's baseline). Any edit saves
            // real times for that person.
            const shown = draft || saved || { start: '12:00', end: '13:00' };
            const dirty =
              !!draft &&
              (draft.start !== (saved?.start || '12:00') ||
                draft.end !== (saved?.end || '13:00'));
            return (
              <div
                key={sp.userId}
                className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500 dark:text-slate-400"
              >
                <span className="min-w-0 flex-1 truncate">{sp.name}</span>
                <TimeField
                  value={shown.start}
                  disabled={busy}
                  onChange={(v) => editDraft(sp, 'start', v)}
                  title="Usual lunch start (saves when you confirm with the check)"
                />
                <span className="text-[10px]">to</span>
                <TimeField
                  value={shown.end}
                  disabled={busy}
                  onChange={(v) => editDraft(sp, 'end', v)}
                  title="Usual lunch end (saves when you confirm with the check)"
                />
                {dirty ? (
                  <>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        onSave(sp.userId, draft.start, draft.end);
                        clearDraft(sp.userId);
                      }}
                      className="flex items-center gap-1 rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-emerald-600 dark:!text-white"
                      title="Save this lunch. Their board updates immediately"
                    >
                      <Check size={10} /> Set
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => clearDraft(sp.userId)}
                      className="rounded-md p-0.5 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                      title="Discard the unsaved change"
                    >
                      <X size={11} />
                    </button>
                  </>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

const initialsFromName = (name) =>
  (name || '?')
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

// Stable per-person colors: the same specialist gets the same color
// everywhere on the calendar. Solid = a planned day, soft tint = the
// default plan.
const AVATAR_COLORS = [
  {
    solid: 'bg-indigo-500 text-white',
    soft: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-300',
  },
  {
    solid: 'bg-emerald-500 text-white',
    soft: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-300',
  },
  {
    solid: 'bg-rose-500 text-white',
    soft: 'bg-rose-100 text-rose-600 dark:bg-rose-500/20 dark:text-rose-300',
  },
  {
    solid: 'bg-amber-400 text-amber-950',
    soft: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300',
  },
  {
    solid: 'bg-violet-500 text-white',
    soft: 'bg-violet-100 text-violet-600 dark:bg-violet-500/20 dark:text-violet-300',
  },
  {
    solid: 'bg-teal-500 text-white',
    soft: 'bg-teal-100 text-teal-600 dark:bg-teal-500/20 dark:text-teal-300',
  },
  {
    solid: 'bg-orange-500 text-white',
    soft: 'bg-orange-100 text-orange-600 dark:bg-orange-500/20 dark:text-orange-300',
  },
];
const colorFor = (userId) => {
  let h = 0;
  for (const ch of String(userId || '')) h = (h * 31 + ch.charCodeAt(0)) % 997;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

// Compact shift text for calendar cells: "8a", "8:30a".
const cellShift = (t) => {
  const [h, m] = hhmm(t).split(':').map(Number);
  if (Number.isNaN(h)) return '';
  const mer = h >= 12 ? 'p' : 'a';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}${m ? `:${String(m).padStart(2, '0')}` : ''}${mer}`;
};

const Initials = ({ name, className = '', colorClass }) => (
  <span
    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
      colorClass ||
      'bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-300'
    } ${className}`}
  >
    {initialsFromName(name)}
  </span>
);

// One scheduled person's row in the day rail: order controls, name, shift
// times, lunch, remove.
const PersonRow = ({
  entry,
  index,
  count,
  busy,
  onMove,
  onTime,
  onLunch,
  onRemove,
  // The person's STANDING lunch ({start, end} 'HH:MM' or null) from the
  // Lunches card — shown when the day carries no override.
  personDefault = null,
  // Org working hours. A shift can only ever NARROW inside these (Nic
  // 2026-08-15): half days are the point, extending past closing is not, and
  // the cadence engine measures every step against the ORG day, so a
  // specialist working "until 6" would just be unmeasured overtime.
  orgStart = DEFAULT_START,
  orgEnd = DEFAULT_END,
  // What this row's hours resolve to today (day row -> the person's usual
  // hours from the Hours card -> org day), computed by DayEditor.
  resolvedShift = null,
}) => {
  // A row with no explicit times INHERITS (2026-09-01): it reads as the
  // person's usual hours, or "Full day" when those are the org day. An
  // explicit pair is a one-day exception, like a lunch override.
  const inherited = !entry.startTime || !entry.endTime;
  const shift = resolvedShift || {
    start: hhmm(entry.startTime) || orgStart,
    end: hhmm(entry.endTime) || orgEnd,
  };
  const isFullDay = shift.start === orgStart && shift.end === orgEnd;
  const [editingTime, setEditingTime] = useState(false);
  // Lunch edits STAGE locally (Nic 2026-08-11): Jorge fiddles the pair,
  // then the emerald check commits — only THEN does it save and push to
  // the specialist's board. Nothing half-edited ever leaves this row.
  const [draftLunch, setDraftLunch] = useState(null); // {start, end} | null
  const savedLunch =
    entry.lunchStartTime && entry.lunchEndTime
      ? { start: hhmm(entry.lunchStartTime), end: hhmm(entry.lunchEndTime) }
      : null;
  const shownLunch = draftLunch || savedLunch;
  const hasLunch = !!shownLunch;
  const lunchDirty =
    !!draftLunch &&
    (draftLunch.start !== savedLunch?.start ||
      draftLunch.end !== savedLunch?.end);
  const editDraftLunch = (changed, value) => {
    const base = draftLunch || savedLunch;
    setDraftLunch(
      pairOnChange({
        start: changed === 'start' ? value : base.start,
        end: changed === 'end' ? value : base.end,
        changed,
        prevStart: base.start,
        prevEnd: base.end,
        follow: true,
      }),
    );
  };
  // Two-step remove: first click arms "Remove?", it disarms itself.
  const [confirmRemove, setConfirmRemove] = useState(false);
  useEffect(() => {
    if (!confirmRemove) return undefined;
    const t = setTimeout(() => setConfirmRemove(false), 3000);
    return () => clearTimeout(t);
  }, [confirmRemove]);

  return (
    <div className="rounded-lg bg-slate-50 px-2 py-1.5 dark:bg-white/5">
      {/* Reorder arrows REMOVED (Nic 2026-08-10): "who gets the first
          lead" wasn't worth the gutter — the rail needs the width more.
          Rotation order = the list order as saved (server positions still
          derive from array order; onMove stays wired for a comeback). */}
      <div className="flex items-center gap-1.5">
        <Initials name={entry.name} colorClass={colorFor(entry.userId).soft} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs font-semibold text-slate-700 dark:text-slate-200">
            {entry.name}
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={() => setEditingTime((v) => !v)}
            className="text-[10px] font-medium text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
            title={
              inherited
                ? 'Following their usual hours (set on the Hours card). Click to set hours for this day only'
                : 'Hours for this day only. Click to edit'
            }
          >
            {inherited && isFullDay
              ? 'Full day'
              : inherited
                ? `Usual: ${formatTime(shift.start)} - ${formatTime(shift.end)}`
                : `${formatTime(shift.start)} - ${formatTime(shift.end)}`}
          </button>
          {/* A one-day override is an exception: one click drops it and the
              row follows their usual hours again (Nic 2026-08-15 / 09-01).
              Hidden when the row already inherits. */}
          {inherited ? null : (
            <button
              type="button"
              disabled={busy}
              onClick={() => onTime(index, 'fullDay', null)}
              title="Drop the one-day hours and follow their usual hours"
              className="ml-1.5 text-[10px] font-medium text-slate-400 underline-offset-2 hover:text-slate-600 hover:underline dark:text-slate-500 dark:hover:text-slate-300"
            >
              Use usual
            </button>
          )}
        </div>
        {/* Removal (Nic 2026-08-10): the old "X" read as "close the time
            picker" and removed people by accident — now a subtle red
            person-remove icon with a two-step confirm (self-resets). */}
        {count === 1 ? (
          <span
            title="Every day needs at least one Sales Rep. Reassign their leads instead of removing them"
            className="rounded-md p-1 text-slate-200 dark:text-slate-700"
          >
            <UserMinus size={13} />
          </span>
        ) : confirmRemove ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => onRemove(index)}
            className="shrink-0 rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-red-600 dark:!text-white"
            title={`Yes, remove ${entry.name} from this day`}
          >
            Remove?
          </button>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={() => setConfirmRemove(true)}
            className="rounded-md p-1 text-red-300 hover:bg-red-50 hover:text-red-500 dark:text-red-400/50 dark:hover:bg-red-500/10 dark:hover:text-red-400"
            title={`Remove ${entry.name} from this day (asks to confirm)`}
          >
            <UserMinus size={13} />
          </button>
        )}
      </div>
      {editingTime ? (
        <div className="mt-1.5 flex items-center gap-1.5 pl-6 text-[10px] text-slate-400 dark:text-slate-500">
          <TimeField
            value={shift.start}
            disabled={busy}
            onChange={(v) => onTime(index, 'startTime', v)}
            minTime={orgStart}
            maxTime={orgEnd}
            title="Shift start"
          />
          <span className="shrink-0">to</span>
          <TimeField
            value={shift.end}
            disabled={busy}
            onChange={(v) => onTime(index, 'endTime', v)}
            minTime={orgStart}
            maxTime={orgEnd}
            title="Shift end"
          />
        </div>
      ) : null}
      {/* Per-person lunch (Nic 2026-08-10): blank rides the org default
          from Working time; a custom pair overrides for this person/day.
          items-start + a nudge keep the icon glued to the FIRST line if
          the row ever wraps. */}
      <div className="mt-1.5 flex items-start gap-1.5 pl-6">
        <Utensils
          size={11}
          className={
            hasLunch
              ? 'mt-[5px] shrink-0 text-violet-500'
              : 'mt-1 shrink-0 text-slate-300 dark:text-slate-600'
          }
        />
        {hasLunch ? (
          <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[10px] text-slate-400 dark:text-slate-500">
            <TimeField
              value={shownLunch.start}
              disabled={busy}
              onChange={(v) => editDraftLunch('start', v)}
              title="Lunch start (saves when you confirm with the check)"
            />
            <span className="shrink-0">to</span>
            <TimeField
              value={shownLunch.end}
              disabled={busy}
              onChange={(v) => editDraftLunch('end', v)}
              title="Lunch end (saves when you confirm with the check)"
            />
            {lunchDirty ? (
              <>
                {/* STAGED: nothing reached the specialist yet — the check
                    commits, the X reverts (Nic 2026-08-11). */}
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    onLunch(index, draftLunch.start, draftLunch.end);
                    setDraftLunch(null);
                  }}
                  className="flex items-center gap-1 rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-emerald-600 dark:!text-white"
                  title="Save this lunch. It updates their board immediately"
                >
                  <Check size={10} /> Set
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setDraftLunch(null)}
                  className="rounded-md p-0.5 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                  title="Discard the unsaved change"
                >
                  <X size={11} />
                </button>
              </>
            ) : (
              <>
                {/* A day override is an EXCEPTION — say so (their standing
                    lunch lives on the Lunches card below). */}
                {savedLunch ? (
                  <span className="shrink-0 rounded-full bg-violet-50 px-1.5 py-px text-[9px] font-semibold text-violet-500 dark:bg-violet-500/15 dark:text-violet-400">
                    for today
                  </span>
                ) : null}
                {savedLunch ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setDraftLunch(null);
                      onLunch(index, null, null);
                    }}
                    className="text-[10px] font-medium text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                    title={
                      personDefault
                        ? 'Drop the one-day override and follow their usual lunch'
                        : 'Drop the custom lunch and follow the org default'
                    }
                  >
                    {personDefault ? 'Use usual' : 'Use default'}
                  </button>
                ) : null}
              </>
            )}
          </span>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={() =>
              // STAGE a draft only — nothing saves until the check.
              setDraftLunch({
                start: personDefault?.start || '12:00',
                end: personDefault?.end || '13:00',
              })
            }
            className="rounded border border-transparent px-1.5 py-0.5 text-[10px] font-medium text-slate-400 hover:border-slate-200 dark:text-slate-500 dark:hover:border-white/10"
            title={
              personDefault
                ? 'Their usual lunch (set on the Lunches card). Click to override for this day only'
                : 'Following the org default lunch. Click to set a custom lunch for this person'
            }
          >
            {personDefault
              ? `Lunch: ${fmt12(personDefault.start)} - ${fmt12(personDefault.end)}`
              : 'Lunch: org default'}
          </button>
        )}
      </div>
    </div>
  );
};

// Its own rail card (Nic 2026-08-09): marking a date off is a deliberate
// org-level act, not a roster tweak — it doesn't belong inside the day's
// rotation editor. Manager-only like everything on this calendar.
const DayOffCard = ({
  date,
  dayOff,
  recurringClosed,
  busy,
  onToggleDayOff,
}) => {
  const [offLabel, setOffLabel] = useState('');
  // A half-typed holiday name must not survive a day change (the card is
  // pinned open at the top of the rail now, so this was easy to hit).
  useEffect(() => {
    setOffLabel('');
  }, [date]);
  return (
    <>
      {dayOff ? (
        <>
          <div className="mb-2 rounded-lg bg-amber-50 px-2.5 py-2 text-[11px] font-medium text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">
            {railDayLabel(date)} is off
            {typeof dayOff === 'string' ? ` (${dayOff})` : ''}. All cadence
            clocks pause; nothing gets scheduled or judged missed.
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={() => onToggleDayOff(date, false)}
            className="rounded-lg bg-slate-100 px-2.5 py-1.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-200 disabled:opacity-50 dark:bg-white/10 dark:text-slate-300 dark:hover:bg-white/15"
          >
            Remove day off
          </button>
        </>
      ) : recurringClosed ? (
        <div className="text-[11px] font-medium text-slate-400 dark:text-slate-500">
          {railDayLabel(date)} is closed every week. Change that under Working
          time below.
        </div>
      ) : (
        <div className="flex items-center gap-1.5">
          <input
            type="text"
            value={offLabel}
            disabled={busy}
            placeholder="Holiday name (optional)"
            onChange={(e) => setOffLabel(e.target.value)}
            className="min-w-0 flex-1 rounded border border-slate-200 bg-transparent px-1.5 py-1 text-[10px] text-slate-600 focus:border-slate-400 focus:outline-none dark:border-white/10 dark:text-slate-300 dark:focus:border-white/30"
          />
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              onToggleDayOff(date, true, offLabel);
              setOffLabel('');
            }}
            className="shrink-0 rounded-lg bg-slate-100 px-2.5 py-1 text-[10px] font-semibold text-slate-500 hover:bg-slate-200 disabled:opacity-50 dark:bg-white/10 dark:text-slate-400 dark:hover:bg-white/15"
          >
            Mark day off
          </button>
        </div>
      )}
    </>
  );
};

const PlainWrap = ({ children }) => <>{children}</>;
const CardWrap = ({ children }) => (
  <div className={`${CARD} p-3`}>{children}</div>
);

// The right rail: the selected day's full editor.
const DayEditor = ({
  date,
  entries,
  notRotating = [],
  specialists,
  busy,
  onSave,
  onReset,
  dayOff,
  recurringClosed,
  // The org working day. Every shift on this page is bounded by it.
  orgStart = DEFAULT_START,
  orgEnd = DEFAULT_END,
  // Inside a RailCard (2026-09-01): the card header carries the date +
  // pills, so the editor renders only its body.
  headless = false,
}) => {
  const isToday = date === localToday();
  const isDefaultPlan = entries.length === 0 && specialists.length > 0;
  // The unplanned-day roster carries NO explicit times (2026-09-01): every
  // row INHERITS - the person's usual hours (Hours card) else the org day -
  // exactly what the server's defaultRoster hands the rotation.
  const shown = isDefaultPlan
    ? specialists.map((s) => ({
        userId: s.userId,
        name: s.name,
        startTime: null,
        endTime: null,
      }))
    : entries;
  // What a row's hours RESOLVE to (day row -> person default -> org day),
  // clamped into the org day like the server does.
  const clampToOrg = (t) => {
    if (minutesOf(t) < minutesOf(orgStart)) return orgStart;
    if (minutesOf(t) > minutesOf(orgEnd)) return orgEnd;
    return t;
  };
  const resolveShift = (entry) => {
    if (entry.startTime && entry.endTime) {
      return { start: hhmm(entry.startTime), end: hhmm(entry.endTime) };
    }
    const sp = specialists.find((s) => s.userId === entry.userId);
    if (sp?.workStartLocal && sp?.workEndLocal) {
      const start = clampToOrg(sp.workStartLocal);
      const end = clampToOrg(sp.workEndLocal);
      if (minutesOf(end) > minutesOf(start)) return { start, end };
    }
    return { start: orgStart, end: orgEnd };
  };
  const available = specialists.filter(
    (w) => !shown.some((e) => e.userId === w.userId),
  );
  const withDefaults = (list) =>
    list.map((e) => ({
      userId: e.userId,
      // Pair or nothing (server-enforced): blank = INHERIT the person's
      // usual hours, else the org day. Never inject constants here - that
      // is what used to freeze every saved day at literal 8-5.
      startTime: hhmm(e.startTime) || null,
      endTime: hhmm(e.endTime) || null,
      // Pair or nothing (server-enforced): blank = the org default lunch.
      lunchStartTime: hhmm(e.lunchStartTime) || null,
      lunchEndTime: hhmm(e.lunchEndTime) || null,
    }));

  const move = (index, dir) => {
    const next = [...shown];
    const [row] = next.splice(index, 1);
    next.splice(index + dir, 0, row);
    onSave(date, withDefaults(next));
  };
  // Someone saved on this day who is no longer a Sales Rep (Nic 2026-10-14):
  // listed, greyed, with Remove as the only action. Saving the day without
  // them clears them; if nobody else was planned, the day goes back to the
  // default plan.
  const removeNotRotating = () => {
    if (isDefaultPlan) onReset(date);
    else onSave(date, withDefaults(entries));
  };
  const remove = (index) => {
    const next = shown.filter((_, i) => i !== index);
    onSave(date, withDefaults(next));
  };
  const setTime = (index, key, value) => {
    const row = shown[index];
    // "Use usual" drops the one-day override: the row goes back to
    // INHERITING (person default -> org day), stored as NULL (2026-09-01).
    if (key === 'fullDay') {
      const next = shown.map((e, i) =>
        i === index ? { ...e, startTime: null, endTime: null } : e,
      );
      onSave(date, withDefaults(next));
      return;
    }
    if (!value) return;
    // An inherited row seeds the pair from its RESOLVED hours, so the first
    // pick edits what the person actually works, not a blank.
    const current = resolveShift(row);
    // Never block a pick: an inverting choice repairs the pair
    // (pairOnChange), keeping the shift's previous length.
    const fixed = pairOnChange({
      start: key === 'startTime' ? value : current.start,
      end: key === 'endTime' ? value : current.end,
      changed: key === 'startTime' ? 'start' : 'end',
      prevStart: current.start,
      prevEnd: current.end,
    });
    // pairOnChange can slide the OTHER end past the org day while keeping the
    // shift's previous length (a 9-5 shift restarted at 4 would want 4-12am).
    // Clamp both ends: a shift never leaves the org working day.
    const clamp = (t) => {
      if (minutesOf(t) < minutesOf(orgStart)) return orgStart;
      if (minutesOf(t) > minutesOf(orgEnd)) return orgEnd;
      return t;
    };
    const start = clamp(fixed.start);
    const end = clamp(fixed.end);
    // A clamp that collapsed the pair (both ends landed on the same bound)
    // would save an invalid shift; leave the row alone instead.
    if (minutesOf(end) <= minutesOf(start)) return;
    const next = shown.map((e, i) =>
      i === index ? { ...e, startTime: start, endTime: end } : e,
    );
    onSave(date, withDefaults(next));
  };
  const add = (userId) => {
    if (!userId) return;
    onSave(date, withDefaults([...shown, { userId }]));
  };
  // Lunch always changes as a PAIR (or clears to the org default) so every
  // instant-save is valid — start picks drag the end along (previous
  // length), end picks never move the start (pairOnChange).
  const setLunch = (index, start, end) => {
    const row = shown[index];
    let pair = { start, end };
    if (start && end) {
      pair = pairOnChange({
        start,
        end,
        changed: start !== hhmm(row.lunchStartTime) ? 'start' : 'end',
        prevStart: hhmm(row.lunchStartTime) || '12:00',
        prevEnd: hhmm(row.lunchEndTime) || '13:00',
        follow: true,
      });
    }
    const next = shown.map((e, i) =>
      i === index
        ? { ...e, lunchStartTime: pair.start, lunchEndTime: pair.end }
        : e,
    );
    onSave(date, withDefaults(next));
  };

  // A day off (specific date) or a weekly-closed day: no roster to manage —
  // the marking/unmarking lives in the dedicated Day off card below.
  if (dayOff || recurringClosed) {
    const closedLine = (
      <div className="text-[11px] font-medium text-slate-400 dark:text-slate-500">
        {dayOff ? 'Day off' : 'Closed every week'}: no rotation to plan.
      </div>
    );
    if (headless) return closedLine;
    return (
      <div className={`${CARD} p-3`}>
        <div className="mb-1 text-sm font-bold text-slate-800 dark:text-slate-100">
          {railDayLabel(date)}
        </div>
        {closedLine}
      </div>
    );
  }

  // Stable wrapper identities (module-level): defining them inline would
  // remount every child on each render and drop PersonRow's local state.
  const Wrap = headless ? PlainWrap : CardWrap;

  return (
    <Wrap>
      {headless ? null : (
        <div className="mb-2">
          <div className="text-sm font-bold text-slate-800 dark:text-slate-100">
            {railDayLabel(date)}
          </div>
          <div className="mt-0.5 flex items-center gap-1.5">
            {isToday ? (
              <span className="rounded-full bg-emerald-50 px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400">
                Today
              </span>
            ) : null}
            {isDefaultPlan ? (
              <span
                title="No plan saved: every specialist rotates all day. Any edit customizes this day."
                className="rounded-full bg-slate-100 px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-slate-400 dark:bg-white/10 dark:text-slate-500"
              >
                Default plan
              </span>
            ) : null}
            <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">
              {shown.length === 0
                ? 'No specialists available'
                : `${shown.length} on rotation`}
            </span>
          </div>
        </div>
      )}

      {shown.length === 0 ? (
        <div className="mb-2 rounded-lg bg-amber-50 px-2.5 py-1.5 text-[10px] font-medium text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">
          No Sales Reps exist, so new leads this day land in
          the shared intake.
        </div>
      ) : (
        <div className="mb-2 space-y-1.5">
          {shown.map((entry, i) => (
            <PersonRow
              key={entry.userId}
              entry={entry}
              index={i}
              count={shown.length}
              busy={busy}
              onMove={move}
              onTime={setTime}
              onLunch={setLunch}
              onRemove={remove}
              orgStart={orgStart}
              orgEnd={orgEnd}
              resolvedShift={resolveShift(entry)}
              personDefault={(() => {
                const sp = specialists.find((s) => s.userId === entry.userId);
                return sp?.lunchStartLocal && sp?.lunchEndLocal
                  ? { start: sp.lunchStartLocal, end: sp.lunchEndLocal }
                  : null;
              })()}
            />
          ))}
        </div>
      )}

      {notRotating.length > 0 ? (
        <div className="mb-2 space-y-1.5">
          {notRotating.map((r) => (
            <div
              key={r.userId}
              className="flex items-center gap-1.5 rounded-lg border border-dashed border-slate-200 px-2 py-1.5 dark:border-white/10"
            >
              <Initials name={r.name} colorClass="bg-slate-100 text-slate-400 dark:bg-white/10 dark:text-slate-500" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-semibold text-slate-400 dark:text-slate-500">
                  {r.name}
                </div>
                <div className="text-[10px] font-medium text-slate-400 dark:text-slate-500">
                  Not a Sales Rep, gets no leads
                </div>
              </div>
              <button
                type="button"
                disabled={busy}
                onClick={removeNotRotating}
                className="shrink-0 rounded-full border border-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-500 hover:border-red-300 hover:text-red-500 dark:border-white/10 dark:text-slate-400 dark:hover:border-red-400/50 dark:hover:text-red-400"
                title={`Remove ${r.name} from this day`}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {available.length > 0 ? (
        <div className="flex items-center gap-1.5">
          <Plus
            size={12}
            className="shrink-0 text-slate-300 dark:text-slate-600"
          />
          <PickerMenu
            label="Add a specialist…"
            disabled={busy}
            onPick={add}
            triggerClassName="rounded border border-transparent px-1.5 py-0.5 text-[10px] font-medium text-slate-400 hover:border-slate-200 dark:text-slate-500 dark:hover:border-white/10"
            options={available.map((w) => ({
              value: w.userId,
              label: w.name,
            }))}
          />
        </div>
      ) : shown.length > 0 ? (
        <div className="px-1.5 text-[10px] font-medium text-slate-300 dark:text-slate-600">
          Every Sales Rep is on this day
        </div>
      ) : null}
    </Wrap>
  );
};

const ScheduleCalendar = ({ open, onClose }) => {
  const queryClient = useQueryClient();
  const today = localToday();
  const [view, setView] = useState(() => {
    const d = new Date();
    return { y: d.getFullYear(), m: d.getMonth() };
  });
  const [selected, setSelected] = useState(today);

  // The 6-week grid (42 cells) anchored on the Sunday before the 1st.
  const cells = useMemo(() => {
    const first = new Date(view.y, view.m, 1);
    const start = new Date(view.y, view.m, 1 - first.getDay());
    return Array.from({ length: 42 }, (_, i) =>
      toIso(
        new Date(start.getFullYear(), start.getMonth(), start.getDate() + i),
      ),
    );
  }, [view]);
  const from = cells[0];
  const to = cells[41];

  const schedule = useFetchBoardSchedule(
    { from, to },
    { enabled: open, keepPreviousData: true },
  );

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['onboarding-board-schedule'] });
    queryClient.invalidateQueries({ queryKey: ['onboarding-board'] });
  };
  const saveDay = useSaveScheduleDay(refresh);
  const saveWorkCal = useSaveWorkCalendar(refresh);
  const saveDayOff = useSaveDayOff(refresh);
  const saveDefaultLunch = useSetUserDefaultLunch(refresh);
  const saveDefaultHours = useSetUserDefaultHours(refresh);
  const resetDay = useResetScheduleDay(refresh);
  const busy =
    saveDay.isLoading ||
    saveWorkCal.isLoading ||
    saveDayOff.isLoading ||
    saveDefaultLunch.isLoading ||
    saveDefaultHours.isLoading ||
    resetDay.isLoading;

  // Rail card open/closed state, remembered per browser (Nic 2026-09-01).
  // Everything starts collapsed; clicking a grid day re-opens the day row
  // (that click IS the intent to edit that day). The first render leaves
  // the remembered state alone.
  const [railOpen, setRailOpen] = useState(readRailOpen);
  const setRail = (id, value) =>
    setRailOpen((prev) => {
      const next = { ...prev, [id]: value };
      writeRailOpen(next);
      return next;
    });
  const toggleRail = (id) => setRail(id, !railOpen[id]);
  const firstSelection = useRef(true);
  useEffect(() => {
    if (firstSelection.current) {
      firstSelection.current = false;
      return;
    }
    setRail('day', true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);
  // Two-step cell reset (one armed cell at a time, self-disarms).
  const [confirmResetIso, setConfirmResetIso] = useState(null);
  useEffect(() => {
    if (!confirmResetIso) return undefined;
    const t = setTimeout(() => setConfirmResetIso(null), 3000);
    return () => clearTimeout(t);
  }, [confirmResetIso]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const data = schedule.data;
  const byDate = useMemo(() => {
    const map = {};
    (data?.days || []).forEach((r) => {
      (map[r.workDate] = map[r.workDate] || []).push(r);
    });
    return map;
  }, [data]);

  const specialists = data?.specialists || [];
  // Saved on a day but no longer a Sales Rep: they get no leads, so the
  // server leaves them out of the day (Nic 2026-10-14); the editor names them.
  const notRotatingByDate = useMemo(() => {
    const map = {};
    (data?.notRotating || []).forEach((r) => {
      (map[r.workDate] = map[r.workDate] || []).push(r);
    });
    return map;
  }, [data]);
  const assignedByDate = data?.assignedByDate || {};
  // Pre-hydration placeholder only. Must match the server defaults
  // (DEFAULT_CADENCE_SETTINGS): it used to say 20:00 / all-seven-days while
  // the server said 17:00 / Mon-Fri, so the first paint showed an org day
  // three hours too long and weekends as working days.
  const workCalendar = data?.workCalendar || {
    workingHoursStartLocal: DEFAULT_START,
    workingHoursEndLocal: DEFAULT_END,
    workingDays: [1, 2, 3, 4, 5],
    daysOff: {},
  };
  const daysOff = workCalendar.daysOff || {};
  const isRecurringClosed = (iso) =>
    !(workCalendar.workingDays || []).includes(isoWeekdayOf(iso));

  const monthLabel = new Date(view.y, view.m, 1).toLocaleDateString([], {
    month: 'long',
    year: 'numeric',
  });
  const navMonth = (dir) => {
    const next = new Date(view.y, view.m + dir, 1);
    setView({ y: next.getFullYear(), m: next.getMonth() });
    // Keep the rail on a visible day.
    setSelected(toIso(next));
  };
  const goToday = () => {
    const d = new Date();
    setView({ y: d.getFullYear(), m: d.getMonth() });
    setSelected(today);
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[1060]">
      <div
        className="absolute inset-0 bg-slate-900/40 dark:bg-black/60"
        onClick={onClose}
        aria-hidden
      />
      <div className="absolute inset-2 flex flex-col overflow-hidden rounded-2xl bg-[#F5F7FA] shadow-2xl dark:bg-surface-page md:inset-6">
        {/* Header */}
        <div className="flex items-center gap-3 bg-white px-4 py-3 shadow-[0_1px_3px_rgba(15,23,42,0.06)] dark:!bg-surface-elevated">
          <div className="flex min-w-0 items-center gap-2 text-base font-bold text-slate-800 dark:text-slate-100">
            <CalendarCog size={17} className="shrink-0 text-slate-400" />
            <span className="truncate">Schedule</span>
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            {/* Today sits LEFT of the arrows: appearing/disappearing there
                never shifts ‹ › under a repeat-clicking cursor. */}
            {selected !== today ||
            view.m !== new Date().getMonth() ||
            view.y !== new Date().getFullYear() ? (
              <button
                type="button"
                onClick={goToday}
                className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-500 hover:bg-slate-200 dark:bg-white/10 dark:text-slate-400 dark:hover:bg-white/15"
              >
                Today
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => navMonth(-1)}
              className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10"
              title="Previous month"
            >
              <ChevronLeft size={15} />
            </button>
            <span className="w-36 text-center text-sm font-semibold text-slate-700 dark:text-slate-200">
              {monthLabel}
            </span>
            <button
              type="button"
              onClick={() => navMonth(1)}
              className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10"
              title="Next month"
            >
              <ChevronRight size={15} />
            </button>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body: month grid + day rail */}
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3 lg:flex-row lg:overflow-hidden">
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="mb-1 grid shrink-0 grid-cols-7">
              {WEEKDAYS.map((w) => (
                <div
                  key={w}
                  className="px-1 pb-1 text-center text-[10px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500"
                >
                  {w}
                </div>
              ))}
            </div>
            <div className="grid flex-1 auto-rows-fr grid-cols-7 gap-1.5">
              {cells.map((iso) => {
                const inMonth =
                  Number(iso.slice(5, 7)) - 1 === view.m &&
                  Number(iso.slice(0, 4)) === view.y;
                const offLabel = daysOff[iso];
                const closedWeekly = isRecurringClosed(iso);
                // Off days render as quiet closed cells — no roster, no
                // tallies; the clocks pause here.
                if (offLabel || closedWeekly) {
                  const isSel = iso === selected;
                  return (
                    <button
                      key={iso}
                      type="button"
                      onClick={() => setSelected(iso)}
                      title={
                        offLabel
                          ? `Day off${typeof offLabel === 'string' ? `: ${offLabel}` : ''}`
                          : 'Closed every week'
                      }
                      className={`flex min-h-[72px] flex-col rounded-lg border border-dashed p-1.5 text-left dark:!border-transparent ${
                        isSel
                          ? 'border-slate-400 bg-slate-50 ring-2 ring-inset ring-slate-400 dark:bg-white/5 dark:ring-slate-400'
                          : 'border-slate-200 bg-slate-50/60 hover:border-slate-300 dark:bg-white/5 dark:hover:ring-1 dark:hover:ring-inset dark:hover:ring-white/25'
                      } ${inMonth ? '' : 'opacity-50'}`}
                    >
                      <span
                        className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${
                          iso === today
                            ? 'bg-slate-900 text-white dark:bg-slate-200 dark:!text-slate-900'
                            : 'text-slate-400 dark:text-slate-500'
                        }`}
                      >
                        {Number(iso.slice(8, 10))}
                      </span>
                      <span className="flex min-w-0 flex-1 items-center justify-center">
                        <span className="truncate text-center text-[12px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                          {offLabel
                            ? typeof offLabel === 'string'
                              ? offLabel
                              : 'Day off'
                            : 'Closed'}
                        </span>
                      </span>
                    </button>
                  );
                }
                const entries = byDate[iso] || [];
                // PAST default days only show people who existed then — a
                // new hire must not retroactively appear on history (saved
                // rows are already frozen facts; this covers the derived
                // default plan). Account creation stands in for the hire
                // date (Nic 2026-08-11).
                const existedBy = (sp) =>
                  !sp.createdAt || toIso(new Date(sp.createdAt)) <= iso;
                const defaultRoster =
                  iso < today ? specialists.filter(existedBy) : specialists;
                const isDefaultPlan =
                  entries.length === 0 && defaultRoster.length > 0;
                const roster = isDefaultPlan ? defaultRoster : entries;
                const isToday = iso === today;
                const isSelected = iso === selected;
                const tooltip = roster
                  .map((r) => {
                    // Explicit day hours only (NULL = inherits, not custom).
                    const shift =
                      !isDefaultPlan && r.startTime && r.endTime
                        ? ` (${formatTime(r.startTime)} - ${formatTime(r.endTime)})`
                        : '';
                    return `${r.name}${shift}`;
                  })
                  .join('\n');
                return (
                  <button
                    key={iso}
                    type="button"
                    onClick={() => setSelected(iso)}
                    title={tooltip || undefined}
                    className={`relative flex min-h-[72px] flex-col rounded-lg p-1.5 text-left transition-shadow ${
                      isSelected
                        ? 'bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_rgba(15,23,42,0.05)] ring-2 ring-inset ring-slate-400 dark:!bg-surface-card dark:ring-slate-400'
                        : 'bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_rgba(15,23,42,0.05)] hover:ring-1 hover:ring-inset hover:ring-slate-300 dark:!bg-surface-card dark:hover:ring-white/25'
                    } ${inMonth ? '' : 'opacity-50'}`}
                  >
                    <div className="mb-1 flex items-center justify-between">
                      <span
                        className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${
                          isToday
                            ? 'bg-slate-900 text-white dark:bg-slate-200 dark:!text-slate-900'
                            : 'text-slate-500 dark:text-slate-400'
                        }`}
                      >
                        {Number(iso.slice(8, 10))}
                      </span>
                      {isDefaultPlan && roster.length > 0 ? (
                        <span className="text-[8px] font-semibold uppercase tracking-wide text-slate-300 dark:text-slate-600">
                          default
                        </span>
                      ) : null}
                    </div>
                    {roster.length === 0 ? (
                      <span className="text-[9px] font-medium text-amber-500/80">
                        No one
                      </span>
                    ) : (
                      (() => {
                        // The cells have real room — use it. Circle size
                        // adapts to how many share the day.
                        const n = roster.length;
                        const size =
                          n <= 2
                            ? 'h-11 w-11 text-sm'
                            : n <= 4
                              ? 'h-9 w-9 text-[11px]'
                              : 'h-7 w-7 text-[9px]';
                        return (
                          <div className="flex flex-1 flex-wrap content-start gap-x-1.5 gap-y-1 pt-0.5">
                            {roster.slice(0, 6).map((r) => {
                              // Explicit day hours mark the cell; an
                              // inheriting row (NULL) never reads as custom.
                              const halfDay =
                                !isDefaultPlan && !!(r.startTime && r.endTime);
                              const color = colorFor(r.userId);
                              return (
                                <span
                                  key={r.userId}
                                  className="flex flex-col items-center gap-0.5"
                                >
                                  <span
                                    className={`relative flex items-center justify-center rounded-full font-bold ${size} ${
                                      isDefaultPlan ? color.soft : color.solid
                                    }`}
                                  >
                                    {r.initials || initialsFromName(r.name)}
                                  </span>
                                  {halfDay ? (
                                    <span className="text-[8px] font-semibold leading-none text-slate-400 dark:text-slate-500">
                                      {cellShift(r.startTime)}-
                                      {cellShift(r.endTime)}
                                    </span>
                                  ) : null}
                                </span>
                              );
                            })}
                            {roster.length > 6 ? (
                              <span className="self-center text-[10px] font-bold text-slate-400">
                                +{roster.length - 6}
                              </span>
                            ) : null}
                          </div>
                        );
                      })()
                    )}
                    {(() => {
                      const dayCounts = assignedByDate[iso];
                      const total = dayCounts
                        ? Object.values(dayCounts).reduce((a, b) => a + b, 0)
                        : 0;
                      // The month view doubles as a load picture: how many
                      // leads the rotation handed out each day.
                      return total > 0 ? (
                        <span className="mt-auto pt-1 text-[9px] font-semibold text-slate-400 dark:text-slate-500">
                          {total} lead{total === 1 ? '' : 's'} assigned
                        </span>
                      ) : null;
                    })()}
                    {/* Reset a TOUCHED day back to the default plan (Nic
                        2026-08-10): editing a day materializes rows, and
                        hand-reverting them can't bring DEFAULT back — this
                        can. Shows once the day has roster rows. Two-step
                        confirm. */}
                    {entries.length > 0 ? (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (busy) return;
                          if (confirmResetIso === iso) {
                            resetDay.mutate({ date: iso });
                            setConfirmResetIso(null);
                          } else {
                            setConfirmResetIso(iso);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') e.currentTarget.click();
                        }}
                        title={
                          confirmResetIso === iso
                            ? 'Yes, reset this day to the default plan'
                            : 'Reset this day to the default plan (asks to confirm)'
                        }
                        className={
                          confirmResetIso === iso
                            ? 'absolute bottom-1 right-1 rounded-full bg-red-500 px-1.5 py-0.5 text-[9px] font-semibold text-white hover:bg-red-600 dark:!text-white'
                            : 'absolute bottom-1 right-1 rounded-md p-1 text-slate-300 hover:bg-slate-100 hover:text-slate-500 dark:text-slate-600 dark:hover:bg-white/10 dark:hover:text-slate-400'
                        }
                      >
                        {confirmResetIso === iso ? (
                          'Reset?'
                        ) : (
                          <RotateCcw size={11} />
                        )}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Day rail. lg:pr-1.5 keeps the scrollbar off the card edges —
              it was overlapping the tiles by a hair (Nic 2026-08-10). */}
          <div className="w-full shrink-0 lg:w-80 lg:overflow-y-auto lg:pr-1.5">
            {schedule.isLoading ? (
              <div className={`${CARD} p-4 text-center text-xs text-slate-400`}>
                Loading schedule…
              </div>
            ) : (
              <div className="space-y-3">
                {/* Premium rail (Nic 2026-09-01): the frequent action (mark a
                    day off) pinned open on top; everything else COLLAPSED
                    with a one-line summary, remembered per browser. No
                    metrics live here ("Today at a glance" is gone). */}
                <RailCard title="Day off" alwaysOpen>
                  <DayOffCard
                    date={selected}
                    dayOff={daysOff[selected]}
                    recurringClosed={isRecurringClosed(selected)}
                    busy={busy}
                    onToggleDayOff={(d, off, label) =>
                      saveDayOff.mutate({ date: d, off, label })
                    }
                  />
                </RailCard>

                {/* Hairline between the org-level Day off action and the
                    selected day's cards (Nic 2026-09-01), same rule as the
                    "Every day" divider below, no label needed. */}
                <div className="pt-1">
                  <span className="block h-px bg-slate-300/70 dark:bg-white/15" />
                </div>

                {(() => {
                  const dayEntries = byDate[selected] || [];
                  const dayOff = daysOff[selected];
                  const closedWeekly = isRecurringClosed(selected);
                  const dayIsDefault =
                    dayEntries.length === 0 && specialists.length > 0;
                  const dayCount = dayEntries.length || specialists.length;
                  const summary =
                    dayOff || closedWeekly ? (
                      <span>{dayOff ? 'Day off' : 'Closed every week'}</span>
                    ) : (
                      <>
                        {selected === today ? (
                          <span className="rounded-full bg-emerald-50 px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400">
                            Today
                          </span>
                        ) : null}
                        {/* No "Default plan" pill up here (Nic 2026-09-01:
                            too much on one row); the editor still shows it
                            once expanded. */}
                        <span
                          title={
                            dayIsDefault
                              ? 'Default plan: every specialist rotates on their usual hours'
                              : undefined
                          }
                        >
                          {dayCount === 0
                            ? 'No specialists'
                            : `${dayCount} on rotation`}
                        </span>
                      </>
                    );
                  return (
                    <RailCard
                      title={railDayLabel(selected)}
                      titleClassName="text-xs font-bold text-slate-800 dark:text-slate-100"
                      keepTitle
                      summary={summary}
                      open={!!railOpen.day}
                      onToggle={() => toggleRail('day')}
                    >
                      <DayEditor
                        headless
                        date={selected}
                        entries={dayEntries}
                        notRotating={notRotatingByDate[selected] || []}
                        specialists={specialists}
                        busy={busy}
                        onSave={(d, users) =>
                          saveDay.mutate({ date: d, users })
                        }
                        onReset={(d) => resetDay.mutate({ date: d })}
                        dayOff={dayOff}
                        recurringClosed={closedWeekly}
                        orgStart={workCalendar.workingHoursStartLocal}
                        orgEnd={workCalendar.workingHoursEndLocal}
                      />
                    </RailCard>
                  );
                })()}

                {/* Everything above this line is about the SELECTED DAY (plus
                    the board-wide knob Jorge reaches for most); below
                    applies to EVERY day (Nic 2026-08-09 — the placement made
                    hours read as per-day; "company-wide" overclaimed the
                    scope, it's the onboarding board's). */}
                <div className="flex items-center gap-2 pt-1">
                  <span className="h-px flex-1 bg-slate-300/70 dark:bg-white/15" />
                  <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                    Every day
                  </span>
                  <span className="h-px flex-1 bg-slate-300/70 dark:bg-white/15" />
                </div>

                <RailCard
                  title="Working time"
                  summary={
                    <>
                      <span>
                        {formatTime(
                          workCalendar.workingHoursStartLocal || DEFAULT_START,
                        )}{' '}
                        -{' '}
                        {formatTime(
                          workCalendar.workingHoursEndLocal || DEFAULT_END,
                        )}
                        {' · '}
                        {daysSummary(workCalendar.workingDays)}
                      </span>
                      {(workCalendar.cadenceConflicts || []).length > 0 ? (
                        <span
                          className="rounded-full bg-amber-50 px-1.5 py-px text-[9px] font-semibold text-amber-600 dark:bg-amber-500/15 dark:text-amber-400"
                          title="Cadence steps scheduled outside these hours"
                        >
                          {workCalendar.cadenceConflicts.length} conflict
                          {workCalendar.cadenceConflicts.length === 1 ? '' : 's'}
                        </span>
                      ) : null}
                    </>
                  }
                  open={!!railOpen.workingTime}
                  onToggle={() => toggleRail('workingTime')}
                >
                  <WorkingTimeCard
                    workCalendar={workCalendar}
                    busy={busy}
                    onSave={(patch) => saveWorkCal.mutate(patch)}
                  />
                </RailCard>

                <RailCard
                  title="Hours"
                  summary={(() => {
                    const custom = specialists.filter(
                      (s) => s.workStartLocal && s.workEndLocal,
                    ).length;
                    return custom
                      ? `${custom} on usual hours`
                      : 'Everyone on the full day';
                  })()}
                  open={!!railOpen.hours}
                  onToggle={() => toggleRail('hours')}
                >
                  <HoursCard
                    specialists={specialists}
                    workCalendar={workCalendar}
                    busy={busy}
                    onSave={(userId, workStartLocal, workEndLocal) =>
                      saveDefaultHours.mutate({
                        userId,
                        workStartLocal,
                        workEndLocal,
                      })
                    }
                  />
                </RailCard>

                <RailCard
                  title="Lunches"
                  summary={(() => {
                    const custom = specialists.filter(
                      (s) => s.lunchStartLocal && s.lunchEndLocal,
                    ).length;
                    return custom ? `${custom} custom` : 'Everyone 12-1';
                  })()}
                  open={!!railOpen.lunches}
                  onToggle={() => toggleRail('lunches')}
                >
                  <LunchesCard
                    specialists={specialists}
                    busy={busy}
                    onSave={(userId, lunchStartLocal, lunchEndLocal) =>
                      saveDefaultLunch.mutate({
                        userId,
                        lunchStartLocal,
                        lunchEndLocal,
                      })
                    }
                  />
                </RailCard>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ScheduleCalendar;
